<template>
    <div
        class="home-v2-welcome-overlay"
        data-testid="home-v2-welcome-overlay"
    >
        <section
            class="home-v2-welcome-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="home-v2-welcome-title"
            aria-describedby="home-v2-welcome-description"
        >
            <button
                type="button"
                class="home-v2-welcome__close"
                data-testid="home-v2-welcome-close"
                aria-label="Close"
                @click="emit('dismiss')"
            >
                <PtIcon icon="SolidCloseMedium" class="home-v2-welcome__close-icon" />
            </button>

            <div class="home-v2-welcome">
                <div class="home-v2-welcome__background" aria-hidden="true">
                    <img :src="backgroundImage" alt="">
                </div>
                <div class="home-v2-welcome__background-fade" aria-hidden="true" />
                <img
                    class="home-v2-welcome__illustration"
                    :src="illustrationImage"
                    alt=""
                    aria-hidden="true"
                >

                <div class="home-v2-welcome__copy">
                    <h2 id="home-v2-welcome-title">
                        {{ title }}
                    </h2>
                    <p id="home-v2-welcome-description">
                        {{ description }}
                    </p>
                </div>

                <div class="home-v2-welcome__actions">
                    <button
                        type="button"
                        class="home-v2-welcome__cta"
                        data-testid="home-v2-welcome-cta"
                        @click="emit('cta')"
                    >
                        {{ cta }}
                    </button>
                    <p>{{ benefit }}</p>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { PtIcon } from 'patsnap-biz';
import '@patsnap-ui/icon/assets/solid/CloseMedium.svg';
import backgroundImage from '../assets/home-v2-welcome-background.png';
import illustrationImage from '../assets/home-v2-welcome-illustration.png';

/** Welcome 弹窗展示属性。 */
export interface HomeV2WelcomeDialogProps {
    /** 弹窗标题。 */
    title: string;
    /** 产品能力说明。 */
    description: string;
    /** 主操作按钮文案。 */
    cta: string;
    /** 免费权益说明。 */
    benefit: string;
}

defineProps<HomeV2WelcomeDialogProps>();

const emit = defineEmits<{
    /** 用户点击主操作按钮。 */
    (event: 'cta'): void;
    /** 用户点击关闭入口或按下 Escape。 */
    (event: 'dismiss'): void;
}>();
</script>

<style lang="scss">
.home-v2-welcome-overlay {
    position: fixed;
    inset: 0;
    z-index: 3000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background-color: rgba(11, 36, 71, 0.56);
    animation: home-v2-welcome-overlay-in 240ms ease-out both;
}

.home-v2-welcome-dialog {
    position: relative;
    width: min(800px, calc(100vw - 32px));
    height: min(480px, calc(100vh - 32px));
    max-height: calc(100vh - 32px);
    overflow-x: hidden;
    overflow-y: auto;
    border-radius: 20px;
    background: #fff;
    box-shadow: 0 4px 8px rgba(7, 100, 233, 0.08), 0 0 4px rgba(7, 100, 233, 0.14);
    animation: home-v2-welcome-dialog-in 520ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

.home-v2-welcome {
    position: relative;
    min-width: 0;
    min-height: 480px;
    overflow: hidden;
    color: #020a1a;
    background: #fff;

    &__close {
        position: absolute;
        top: 16px;
        right: 16px;
        z-index: 3;
        display: flex;
        width: 24px;
        height: 24px;
        align-items: center;
        justify-content: center;
        padding: 0;
        color: #495973;
        cursor: pointer;
        background: transparent;
        border: 0;
        border-radius: 4px;

        &:hover {
            background: rgba(7, 100, 233, 0.08);
        }

        .home-v2-welcome__close-icon {
            width: 14px;
            height: 14px;
        }
    }

    &__background,
    &__background-fade {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 105px;
        mask: url('../assets/home-v2-welcome-mask.svg') top left / 100% 104px no-repeat;
        animation: home-v2-welcome-background-in 720ms ease-out 80ms both;
    }

    &__background {
        overflow: hidden;
        transform: rotate(180deg);

        img {
            position: absolute;
            top: -174%;
            left: -0.25%;
            width: 100.5%;
            height: 300%;
            max-width: none;
            opacity: 0.3;
        }
    }

    &__background-fade {
        height: 110px;
        mask: none;
        background: linear-gradient(to bottom, rgba(255, 255, 255, 0), #fff);
    }

    &__illustration {
        position: absolute;
        top: 54px;
        left: 50%;
        width: 284px;
        height: 100px;
        max-width: calc(100% - 32px);
        object-fit: cover;
        object-position: left top;
        transform: translateX(-50%);
        animation: home-v2-welcome-illustration-in 700ms cubic-bezier(0.22, 1, 0.36, 1) 100ms both;
    }

    &__copy {
        position: absolute;
        top: 194px;
        left: 50%;
        display: flex;
        width: min(720px, calc(100% - 80px));
        flex-direction: column;
        align-items: center;
        gap: 24px;
        text-align: center;
        transform: translateX(-50%);
        animation: home-v2-welcome-content-in 560ms cubic-bezier(0.22, 1, 0.36, 1) 220ms both;

        h2 {
            width: 100%;
            margin: 0;
            font-family: 'PingFang SC', sans-serif;
            font-size: 32px;
            font-weight: 600;
            line-height: 40px;
            overflow-wrap: anywhere;
        }

        p {
            width: min(427px, 100%);
            margin: 0;
            color: #495973;
            font-family: 'ABeeZee', sans-serif;
            font-size: 20px;
            font-weight: 400;
            line-height: 32px;
            overflow-wrap: anywhere;
        }
    }

    &__actions {
        position: absolute;
        top: 346px;
        left: 50%;
        display: flex;
        width: min(720px, calc(100% - 80px));
        flex-direction: column;
        align-items: center;
        transform: translateX(-50%);
        animation: home-v2-welcome-content-in 560ms cubic-bezier(0.22, 1, 0.36, 1) 340ms both;

        p {
            width: min(500px, 100%);
            margin: 10px 0 0;
            color: #afafaf;
            font-family: 'ABeeZee', sans-serif;
            font-size: 16px;
            font-weight: 400;
            line-height: 24px;
            text-align: center;
            overflow-wrap: anywhere;
        }
    }

    &__cta {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 40px;
        margin: 0;
        padding: 8px 28px;
        color: #fff;
        font-family: 'ABeeZee', sans-serif;
        font-size: 16px;
        font-weight: 400;
        line-height: 24px;
        white-space: normal;
        background: #0764e9;
        border-color: #0764e9;
        border-radius: 4px;
        cursor: pointer;
        box-shadow: 0 8px 20px rgba(7, 100, 233, 0.18);
        transition: background-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;

        &:hover {
            background: #0456d1;
            border-color: #0456d1;
            box-shadow: 0 10px 24px rgba(7, 100, 233, 0.24);
            transform: translateY(-1px);
        }

        &:active {
            box-shadow: 0 4px 12px rgba(7, 100, 233, 0.2);
            transform: translateY(0);
        }
    }
}

.home-v2-welcome-host--closing {
    .home-v2-welcome-overlay {
        pointer-events: none;
        animation: home-v2-welcome-overlay-out 320ms ease-in both;
    }

    .home-v2-welcome-dialog {
        animation: home-v2-welcome-dialog-out 320ms cubic-bezier(0.4, 0, 1, 1) both;
    }
}

@keyframes home-v2-welcome-overlay-in {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes home-v2-welcome-overlay-out {
    from { opacity: 1; }
    to { opacity: 0; }
}

@keyframes home-v2-welcome-dialog-in {
    from {
        opacity: 0;
        transform: translateY(24px) scale(0.96);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

@keyframes home-v2-welcome-dialog-out {
    from {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
    to {
        opacity: 0;
        transform: translateY(-10px) scale(0.98);
    }
}

@keyframes home-v2-welcome-background-in {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes home-v2-welcome-illustration-in {
    from {
        opacity: 0;
        transform: translate(-50%, 18px) scale(0.9);
    }
    to {
        opacity: 1;
        transform: translate(-50%, 0) scale(1);
    }
}

@keyframes home-v2-welcome-content-in {
    from {
        opacity: 0;
        transform: translate(-50%, 14px);
    }
    to {
        opacity: 1;
        transform: translate(-50%, 0);
    }
}

@media (prefers-reduced-motion: reduce) {
    .home-v2-welcome-overlay,
    .home-v2-welcome-dialog,
    .home-v2-welcome__background,
    .home-v2-welcome__background-fade,
    .home-v2-welcome__illustration,
    .home-v2-welcome__copy,
    .home-v2-welcome__actions {
        animation-duration: 1ms !important;
        animation-delay: 0ms !important;
    }

    .home-v2-welcome__cta {
        transition: none;
    }
}
</style>
